import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AlertColor,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandIcon } from '@mui/icons-material';
import { jobSourcesApi } from '../../services/api';
import { AtsPlatform, IngestSettings, IngestSettingsPatch } from '../../types';
import { errorMessage, fieldErrors } from '../../utils/errors';
import { formatDateTime } from '../../utils/format';
import { atsLabel } from '../../utils/jobBoard';
import {
  ATS_PLATFORMS,
  INTERVAL_PRESETS,
  MAX_INTERVAL_HOURS,
  MIN_INTERVAL_HOURS,
  intervalHint,
  intervalLabel,
  parseWholeNumber,
  schedulerCadence,
} from '../../utils/jobSources';

interface Props {
  settings: IngestSettings;
  /** True while a pause or resume is being saved. */
  pausing: boolean;
  onSetPaused: (paused: boolean) => void;
  /** Called with the settings as the API returned them after a save. */
  onSaved: (settings: IngestSettings) => void;
  notify: (message: string, severity?: AlertColor) => void;
}

/** What has been edited and not saved yet. Number fields keep the typed text. */
interface Draft {
  interval?: string;
  platforms?: AtsPlatform[];
  maxBoards?: string;
  retire?: string;
}

const WHOLE_NUMBER = 'Enter a whole number.';
const ADVANCED_FIELDS = ['max_boards_per_run', 'retire_after_failures'];

const samePlatforms = (a: AtsPlatform[], b: AtsPlatform[]): boolean =>
  a.length === b.length && a.every((platform) => b.includes(platform));

/**
 * How the job board is kept fresh. Pausing takes effect at once; the other
 * settings are edited as a draft and saved together.
 */
const SourcesSettings: React.FC<Props> = ({ settings, pausing, onSetPaused, onSaved, notify }) => {
  const [draft, setDraft] = useState<Draft>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const saved = {
    interval: String(settings.refresh_interval_hours),
    platforms: settings.enabled_platforms ?? [],
    maxBoards: String(settings.max_boards_per_run),
    retire: String(settings.retire_after_failures),
  };
  const interval = draft.interval ?? saved.interval;
  const platforms = draft.platforms ?? saved.platforms;
  const maxBoards = draft.maxBoards ?? saved.maxBoards;
  const retire = draft.retire ?? saved.retire;

  const changed = {
    interval: interval.trim() !== saved.interval,
    platforms: !samePlatforms(platforms, saved.platforms),
    maxBoards: maxBoards.trim() !== saved.maxBoards,
    retire: retire.trim() !== saved.retire,
  };
  const dirty = changed.interval || changed.platforms || changed.maxBoards || changed.retire;

  const edit = (patch: Draft, field: string) => {
    setDraft((prev) => ({ ...prev, ...patch }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const togglePlatform = (platform: AtsPlatform, on: boolean) => {
    const next = ATS_PLATFORMS.filter((p) => (p === platform ? on : platforms.includes(p)));
    edit({ platforms: next }, 'enabled_platforms');
  };

  const reset = () => {
    setDraft({});
    setErrors({});
  };

  const save = async () => {
    const patch: IngestSettingsPatch = {};
    const problems: Record<string, string> = {};
    const readNumber = (text: string, field: 'refresh_interval_hours' | 'max_boards_per_run' | 'retire_after_failures') => {
      const value = parseWholeNumber(text);
      if (value === null) problems[field] = WHOLE_NUMBER;
      else patch[field] = value;
    };
    if (changed.interval) readNumber(interval, 'refresh_interval_hours');
    if (changed.maxBoards) readNumber(maxBoards, 'max_boards_per_run');
    if (changed.retire) readNumber(retire, 'retire_after_failures');
    if (changed.platforms) {
      if (platforms.length === 0) {
        problems.enabled_platforms = 'Keep at least one ATS enabled. To stop all refreshing, pause it instead.';
      } else {
        patch.enabled_platforms = platforms;
      }
    }
    if (Object.keys(problems).length) {
      setErrors(problems);
      if (ADVANCED_FIELDS.some((field) => problems[field])) setAdvancedOpen(true);
      return;
    }

    setSaving(true);
    setErrors({});
    try {
      const next = await jobSourcesApi.patchSettings(patch);
      setDraft({});
      onSaved(next);
      notify('Settings saved.');
    } catch (e) {
      const byField = fieldErrors(e);
      if (Object.keys(byField).length) {
        setErrors(byField);
        if (ADVANCED_FIELDS.some((field) => byField[field])) setAdvancedOpen(true);
        // A message for a field this form does not show still has to be seen.
        const shown = ['refresh_interval_hours', 'enabled_platforms', ...ADVANCED_FIELDS];
        const stray = Object.keys(byField).find((field) => !shown.includes(field));
        notify(stray ? `${stray}: ${byField[stray]}` : 'Some settings were not accepted. See the messages by the fields.', 'error');
      } else {
        notify(errorMessage(e, 'Could not save the settings.'), 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const intervalNumber = parseWholeNumber(interval);
  const preset = intervalNumber !== null && INTERVAL_PRESETS.includes(intervalNumber) ? intervalNumber : null;

  return (
    <Paper variant="outlined" component="section" aria-labelledby="sources-settings-title" sx={{ p: 2 }}>
      <Typography id="sources-settings-title" variant="h6" component="h2" sx={{ fontWeight: 700 }}>
        Refresh settings
      </Typography>
      {settings.updated_at && (
        <Typography variant="caption" color="text.secondary">
          Last changed {formatDateTime(settings.updated_at)}
        </Typography>
      )}

      <Box sx={{ mt: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <FormControlLabel
            control={
              <Switch
                checked={!settings.paused}
                disabled={pausing}
                onChange={(e) => onSetPaused(!e.target.checked)}
              />
            }
            label={settings.paused ? 'Scheduled refreshing is paused' : 'Scheduled refreshing is on'}
          />
          {pausing && <CircularProgress size={18} aria-label="Saving" />}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Pausing stops the scheduler from reading boards. Refreshing by hand still works.
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box
        component="form"
        noValidate
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
          if (dirty && !saving) save();
        }}
      >
        <Typography id="sources-interval-label" variant="subtitle2" component="h3" sx={{ fontWeight: 700 }}>
          Refresh interval
        </Typography>
        <Typography id="sources-interval-help" variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          The scheduler checks {schedulerCadence()}, and this interval decides how often each board is actually re-read.
        </Typography>
        <Stack direction="row" alignItems="flex-start" sx={{ flexWrap: 'wrap', gap: 1.5 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={preset}
            onChange={(_, value: number | null) => {
              if (value !== null) edit({ interval: String(value) }, 'refresh_interval_hours');
            }}
            aria-label="Refresh interval presets"
          >
            {INTERVAL_PRESETS.map((hours) => (
              <ToggleButton key={hours} value={hours} aria-label={`Every ${intervalLabel(hours)}`} sx={{ px: 1.5, textTransform: 'none' }}>
                {hours}h
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <TextField
            label="Hours"
            type="number"
            size="small"
            value={interval}
            onChange={(e) => edit({ interval: e.target.value }, 'refresh_interval_hours')}
            error={Boolean(errors.refresh_interval_hours)}
            helperText={errors.refresh_interval_hours || intervalHint(intervalNumber)}
            inputProps={{
              min: MIN_INTERVAL_HOURS,
              max: MAX_INTERVAL_HOURS,
              step: 1,
              inputMode: 'numeric',
              'aria-describedby': 'sources-interval-help',
            }}
            sx={{ width: 190 }}
          />
        </Stack>

        <FormControl
          component="fieldset"
          error={Boolean(errors.enabled_platforms)}
          sx={{ mt: 2, display: 'block' }}
        >
          <FormLabel component="legend" sx={{ typography: 'subtitle2', fontWeight: 700, color: 'text.primary' }}>
            ATS to refresh
          </FormLabel>
          <FormGroup row>
            {ATS_PLATFORMS.map((platform) => (
              <FormControlLabel
                key={platform}
                control={
                  <Checkbox
                    size="small"
                    checked={platforms.includes(platform)}
                    onChange={(e) => togglePlatform(platform, e.target.checked)}
                  />
                }
                label={atsLabel(platform)}
              />
            ))}
          </FormGroup>
          <FormHelperText sx={{ mx: 0 }}>
            {errors.enabled_platforms || 'Boards on an ATS that is not ticked are skipped by the scheduler.'}
          </FormHelperText>
        </FormControl>

        <Accordion
          expanded={advancedOpen}
          onChange={(_, open) => setAdvancedOpen(open)}
          disableGutters
          elevation={0}
          sx={{ mt: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandIcon />} aria-controls="sources-advanced" id="sources-advanced-header">
            <Typography variant="body2" color="text.secondary">
              Advanced
              {changed.maxBoards || changed.retire ? ' (edited)' : ''}
            </Typography>
          </AccordionSummary>
          <AccordionDetails id="sources-advanced">
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Most boards per scheduler call"
                type="number"
                size="small"
                fullWidth
                value={maxBoards}
                onChange={(e) => edit({ maxBoards: e.target.value }, 'max_boards_per_run')}
                error={Boolean(errors.max_boards_per_run)}
                helperText={errors.max_boards_per_run || '0 means no cap.'}
                inputProps={{ min: 0, max: 2000, step: 1, inputMode: 'numeric' }}
              />
              <TextField
                label="Failures before a board is switched off"
                type="number"
                size="small"
                fullWidth
                value={retire}
                onChange={(e) => edit({ retire: e.target.value }, 'retire_after_failures')}
                error={Boolean(errors.retire_after_failures)}
                helperText={errors.retire_after_failures || 'Failed reads in a row.'}
                inputProps={{ min: 1, max: 50, step: 1, inputMode: 'numeric' }}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
          <Button type="submit" variant="contained" disabled={!dirty || saving}>
            {saving ? <CircularProgress size={20} color="inherit" aria-label="Saving" /> : 'Save changes'}
          </Button>
          <Button onClick={reset} disabled={!dirty || saving}>
            Discard
          </Button>
          {dirty && !saving && (
            <Typography variant="caption" color="text.secondary">
              Unsaved changes
            </Typography>
          )}
        </Stack>
      </Box>
    </Paper>
  );
};

export default SourcesSettings;
